import { useEffect, useMemo, useRef, useState } from 'react';

import Modal from '@/components/Modal';
import ThemedIconButton from '@/components/ThemedIconButton';
import { setShareModalOpen, setStartShare } from '@/state/features/app';
import { useAppDispatch, useAppSelector } from '@/state/store';
import * as styles from './styles.module.scss';

import { sendEvent } from '@/utils/analytics';
import { isDiscordOnMobile } from '@/components/DiscordAuthFlow/useDiscord';
import { IS_DISCORD_ACTIVITY } from '@/globals';

export default function ShareModal() {
    const dispatch = useAppDispatch();
    const { shareModalOpen, shareMessage, shareURL, shareModalTitle } = useAppSelector((state) => state.app);

    const [copyText, setCopyText] = useState('Copy');
    const copyTextTimeout = useRef<number>(0);

    const url = encodeURIComponent(shareURL);
    const title = encodeURIComponent(shareMessage);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&title=${title}`;
    const twitterUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;

    function open(url: string) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    // Track share button click
    function trackShare(target: string) {
        sendEvent('share_button_click', { shareTarget: target });
    }

    useEffect(() => {
        if (shareModalOpen) {
            sendEvent('share_modal_open');
        }
    }, [shareModalOpen]);

    const isDiscordMobile = useMemo(() => {
        try {
            return IS_DISCORD_ACTIVITY && isDiscordOnMobile();
        } catch (error) {
            return false;
        }
    }, []);

    return (
        <Modal
            title={shareModalTitle}
            onClose={() => {
                dispatch(setShareModalOpen(false));
            }}
            isVisible={shareModalOpen}
            zIndex={130}
        >
            <div className={styles.buttonContainer}>
                {!isDiscordMobile && (
                    <>
                        <ThemedIconButton
                            text="Share"
                            onClick={() => {
                                trackShare('facebook');
                                open(facebookUrl);
                            }}
                            disabled={false}
                            icon="facebook"
                        />
                        <ThemedIconButton
                            text="Share"
                            onClick={() => {
                                trackShare('twitter');
                                open(twitterUrl);
                            }}
                            disabled={false}
                            icon="twitter"
                        />
                    </>
                )}
                <ThemedIconButton
                    text={copyText}
                    onClick={() => {
                        try {
                            const copyMessage = shareMessage + ' \n' + shareURL;
                            navigator.clipboard.writeText(copyMessage);
                            setCopyText('Copied!');
                            clearTimeout(copyTextTimeout.current);
                            copyTextTimeout.current = setTimeout(() => {
                                setCopyText('Copy');
                            }, 2000) as any as number;

                            trackShare('copy');
                        } catch (error) {
                            console.error('Failed to copy: ', error);
                        }
                    }}
                    disabled={false}
                    icon="copy"
                />
            </div>
        </Modal>
    );
}
